import type { ReactNode } from 'react';
import { SearchableDropdown } from '@tutor/ui';

export const Field = ({ label, error, hint, children }: { label: string; error?: string; hint?: string; children: ReactNode }) => (
  <div className="space-y-2">
    <label className="block text-sm font-semibold text-gray-800">{label}</label>
    {children}
    {hint && !error && <p className="text-xs text-gray-600">{hint}</p>}
    {error && <p role="alert" className="text-sm font-medium text-red-600">{error}</p>}
  </div>
);

export const TextInput = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input
    {...props}
    className={`min-h-11 w-full rounded-lg border border-gray-300 px-3 py-2 text-base outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20 ${props.className ?? ''}`}
  />
);

export const DropdownInput = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  disabled = false,
  searchable = true,
  allowCustom = false,
}: {
  options: string[] | Array<{ value: string | number; label: string }>;
  value: string | number;
  onChange: (value: string | number) => void;
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  allowCustom?: boolean;
}) => (
  <SearchableDropdown
    options={options}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    disabled={disabled}
    searchable={searchable}
    allowCustom={allowCustom}
  />
);

export const TextArea = (props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea
    {...props}
    className={`w-full rounded-lg border border-gray-300 px-3 py-2 text-base outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20 ${props.className ?? ''}`}
  />
);

export const ChoiceButtons = <T extends string>({
  options,
  value,
  onChange,
}: {
  options: ReadonlyArray<{ value: T; label: string }>;
  value: T | '';
  onChange: (value: T) => void;
}) => (
  <div className="grid gap-2 sm:grid-cols-2">
    {options.map((option) => {
      const selected = value === option.value;
      return (
        <button
          key={option.value}
          type="button"
          aria-pressed={selected}
          onClick={() => onChange(option.value)}
          className={`min-h-11 cursor-pointer rounded-lg border px-4 py-2 text-left font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${selected ? 'border-primary bg-primary-50 text-primary-800' : 'border-gray-300 text-gray-700 hover:border-primary/50 hover:bg-gray-50'}`}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

export const CheckboxList = ({
  options,
  value,
  onChange,
}: {
  options: ReadonlyArray<{ value: string; label: string }>;
  value: string[];
  onChange: (value: string[]) => void;
}) => (
  <div className="grid gap-2 sm:grid-cols-2">
    {options.map((option) => {
      const selected = value.includes(option.value);
      return (
        <label key={option.value} className={`flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 transition-colors ${selected ? 'border-primary bg-primary-50' : 'border-gray-300 hover:bg-gray-50'}`}>
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onChange(selected ? value.filter((item) => item !== option.value) : [...value, option.value])}
            className="h-4 w-4 accent-primary focus:ring-primary"
          />
          <span className="text-sm font-medium text-gray-800">{option.label}</span>
        </label>
      );
    })}
  </div>
);

export const StepGrid = ({ children }: { children: ReactNode }) => (
  <div className="grid gap-5 md:grid-cols-2">{children}</div>
);
